import { Icon } from '@99designs/common/components/Icon';
import { StructuredBriefFile } from '__generated__/StructuredBriefFile';
import { WebBuilderAssets, WebBuilderPlatform } from '__generated__/types';
import { WebBuilderBrief } from '__generated__/WebBuilderBrief';
import { WebBuilderBriefFields } from '__generated__/WebBuilderBriefFields';
import Mediabox from 'common/components/Mediabox';
import { Currency } from 'common/utils/currency';
import { BudgetSummary } from 'projects/components/BriefFields/BudgetField';
import ContentSetting from 'projects/components/ContentSetting';
import { formatAndLinkify } from 'projects/components/FormatAndLinkify';
import { linkify } from 'projects/components/Linkify';
import { Matrix, MatrixItem } from 'projects/components/Matrix';
import SummaryCard from 'projects/components/summary/SummaryCard';
import SummaryCardItem from 'projects/components/summary/SummaryCardItem';
import { TimeframeSummary } from 'projects/components/TimeFrame';
import { trackEditBriefEvent, trackViewedBriefEvent } from 'projects/events';
import { routes } from 'projects/routes';
import { Industries } from 'projects/util/industries';
import React, { useEffect } from 'react';

export interface Props {
    brief: WebBuilderBrief;
    industries: Industries;
}

const assets = (assets: WebBuilderAssets[]) => {
    return assets.map((asset, id) => {
        switch (asset) {
            case WebBuilderAssets.LOGO:
                return <div key={id}> {__('Logo & Branding')} </div>;
            case WebBuilderAssets.IMAGES_VIDEO:
                return <div key={id}> {__('Images & Visual Assets')} </div>;
            case WebBuilderAssets.COPY:
                return <div key={id}> {__('Website & Product Copy')} </div>;
        }
    });
};

const platform = (platform: WebBuilderPlatform) => {
    switch (platform) {
        case WebBuilderPlatform.JIMDO:
            return 'Jimdo';
        case WebBuilderPlatform.MAGENTO:
            return 'Magento';
        case WebBuilderPlatform.SHOPIFY:
            return 'Shopify';
        case WebBuilderPlatform.WEEBLY:
            return 'Weebly';
        case WebBuilderPlatform.WIX:
            return 'Wix';
        case WebBuilderPlatform.WOOCOMMERCE:
            return 'Woocommerce';
        case WebBuilderPlatform.WORDPRESS:
            return 'Wordpress';
    }
};

function SummaryPage({ brief, industries }: Props) {
    const fields = brief.fields as WebBuilderBriefFields;

    useEffect(() => {
        trackViewedBriefEvent({
            id: brief.id,
            page: 'Summary',
        });
    }, []);

    function handleEditClick(page: 'Details') {
        trackEditBriefEvent({
            id: brief.id,
            page: page,
        });
    }

    return (
        <ContentSetting>
            <SummaryCard
                heading={__('Tell us about your project')}
                onEditClick={() => handleEditClick('Details')}
                url={routes.brief(brief, 1)}
            >
                {fields.platform !== WebBuilderPlatform.NOT_SURE && (
                    <SummaryCardItem title={__('Web builder platform')}>
                        {platform(fields.platform)}
                    </SummaryCardItem>
                )}

                <SummaryCardItem title={__('Name your project')}>{fields.title}</SummaryCardItem>

                <SummaryCardItem title={__('Describe your project')}>
                    {formatAndLinkify(fields.description)}
                </SummaryCardItem>

                {fields.referenceFiles.length > 0 && (
                    <SummaryCardItem title={__('Add reference files')}>
                        <Matrix columnOverrides={{ large: 3 }}>
                            {fields.referenceFiles.map(
                                (attachment: StructuredBriefFile, id: number) => {
                                    return (
                                        <MatrixItem key={id}>
                                            <Mediabox>
                                                <div className="uploader-attachment-overlay">
                                                    <div className="uploader-attachment-overlay__inner">
                                                        <div className="uploader-attachment-overlay__content">
                                                            {attachment.previewable ? (
                                                                <img
                                                                    src={`https://${
                                                                        attachment.bucket
                                                                    }.s3-accelerate.amazonaws.com/${
                                                                        attachment.key
                                                                    }`}
                                                                    role="presentational"
                                                                />
                                                            ) : (
                                                                <Icon
                                                                    type={'document-1'}
                                                                    variations={['2x']}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </Mediabox>
                                        </MatrixItem>
                                    );
                                }
                            )}
                        </Matrix>
                    </SummaryCardItem>
                )}
                {fields.inspirationFiles.length > 0 && (
                    <SummaryCardItem title={__('Add inspiration files')}>
                        <Matrix columnOverrides={{ large: 3 }}>
                            {fields.inspirationFiles.map(
                                (attachment: StructuredBriefFile, id: number) => {
                                    return (
                                        <MatrixItem key={id}>
                                            <Mediabox>
                                                <div className="uploader-attachment-overlay">
                                                    <div className="uploader-attachment-overlay__inner">
                                                        <div className="uploader-attachment-overlay__content">
                                                            {attachment.previewable ? (
                                                                <img
                                                                    src={`https://${
                                                                        attachment.bucket
                                                                    }.s3-accelerate.amazonaws.com/${
                                                                        attachment.key
                                                                    }`}
                                                                    role="presentational"
                                                                />
                                                            ) : (
                                                                <Icon
                                                                    type={'document-1'}
                                                                    variations={['2x']}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </Mediabox>
                                        </MatrixItem>
                                    );
                                }
                            )}
                        </Matrix>
                    </SummaryCardItem>
                )}
                {fields.inspirationUrls.length > 0 && (
                    <SummaryCardItem title={__('Link to any inspiration on the Web')}>
                        {linkify(fields.inspirationUrls)}
                    </SummaryCardItem>
                )}
                {fields.url !== '' && (
                    <SummaryCardItem title={__('Link to your existing website, if you have one')}>
                        {formatAndLinkify(fields.url)}
                    </SummaryCardItem>
                )}
                {fields.assets.length > 0 && (
                    <SummaryCardItem title={__('Do you have any of the following?')}>
                        {assets(fields.assets)}
                    </SummaryCardItem>
                )}
            </SummaryCard>
            <SummaryCard
                heading={__('Your timeline')}
                onEditClick={() =>
                    trackEditBriefEvent({
                        id: brief.id,
                        page: 'TimeAndBudget',
                    })
                }
                url={routes.brief(brief, 2)}
            >
                <SummaryCardItem title={__("What's your timeline?")}>
                    <TimeframeSummary
                        value={fields.timeframe}
                        startDate={fields.startDate}
                        endDate={fields.endDate}
                    />
                </SummaryCardItem>
                <SummaryCardItem title={__("What's your budget?")}>
                    <BudgetSummary budget={fields.budget} currency={fields.currency as Currency} />
                </SummaryCardItem>
            </SummaryCard>
            <SummaryCard
                heading={__('About you')}
                onEditClick={() =>
                    trackEditBriefEvent({
                        id: brief.id,
                        page: 'AboutYou',
                    })
                }
                url={routes.brief(brief, 3)}
            >
                {industries.find(fields.industry) && (
                    <SummaryCardItem title={__('What industry are you in?')}>
                        {industries.find(fields.industry)}
                    </SummaryCardItem>
                )}
                <SummaryCardItem
                    title={__(
                        'Can your designer showcase the finished designs in their 99designs portfolio?'
                    )}
                >
                    {fields.profileApproval ? __('Yes') : __('No')}
                </SummaryCardItem>
                {fields.other != '' && (
                    <SummaryCardItem
                        title={__("Anything else you'd like to share with your designer?")}
                    >
                        {formatAndLinkify(fields.other)}
                    </SummaryCardItem>
                )}
            </SummaryCard>
        </ContentSetting>
    );
}

export default SummaryPage;
